import {
  CalendarDateRange,
  CalendarViewMode,
  ClassCalendarItem,
} from '../types/ClassCalendar.type';

export const CLASS_CALENDAR_TIME_ZONE = 'Asia/Bangkok';
export const CALENDAR_WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const isDateKey = (value: string): boolean => {
  if (!DATE_KEY_PATTERN.test(value)) return false;

  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

export const dateKeyFromDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const dateFromKey = (key: string): Date => {
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const addCalendarDays = (date: Date, amount: number): Date => {
  const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  result.setDate(result.getDate() + amount);
  return result;
};

export const addCalendarMonths = (date: Date, amount: number): Date =>
  new Date(date.getFullYear(), date.getMonth() + amount, 1);

export const startOfSundayWeek = (date: Date): Date =>
  addCalendarDays(date, -date.getDay());

export const getCalendarDateKeys = (
  anchorDate: string,
  view: CalendarViewMode,
): string[] => {
  const anchor = dateFromKey(anchorDate);

  if (view === 'week') {
    const start = startOfSundayWeek(anchor);
    return Array.from({ length: 7 }, (_, index) =>
      dateKeyFromDate(addCalendarDays(start, index)),
    );
  }

  const firstOfMonth = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const gridStart = startOfSundayWeek(firstOfMonth);
  const lastOfMonth = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  const gridEnd = addCalendarDays(lastOfMonth, 6 - lastOfMonth.getDay());
  const calculatedDays =
    Math.round((gridEnd.getTime() - gridStart.getTime()) / 86400000) + 1;
  const totalDays = Math.max(35, calculatedDays);

  return Array.from({ length: totalDays }, (_, index) =>
    dateKeyFromDate(addCalendarDays(gridStart, index)),
  );
};

export const getCalendarDateRange = (
  anchorDate: string,
  view: CalendarViewMode,
): CalendarDateRange => {
  const visibleDates = getCalendarDateKeys(anchorDate, view);
  const lastDate = dateFromKey(visibleDates[visibleDates.length - 1]);
  return {
    from: visibleDates[0],
    toExclusive: dateKeyFromDate(addCalendarDays(lastDate, 1)),
  };
};

export const getEventDateKey = (item: ClassCalendarItem): string | null => {
  if (!item.classDate) return null;
  const dateKey = item.classDate.slice(0, 10);
  return isDateKey(dateKey) ? dateKey : null;
};

export const timeToMinutes = (
  value: string | null | undefined,
): number | null => {
  if (!value) return null;
  const match = /^(\d{1,2}):(\d{2})/.exec(value.trim());
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
};

export const formatCalendarTime = (
  value: string | null | undefined,
): string => {
  if (!value) return '--:--';
  const match = /^(\d{1,2}):(\d{2})/.exec(value.trim());
  if (!match) return value;
  return `${String(Number(match[1])).padStart(2, '0')}:${match[2]}`;
};

export const formatCalendarTimeRange = (
  startTime: string | null | undefined,
  endTime: string | null | undefined,
): string => {
  if (!startTime && !endTime) return 'Time not set';
  return `${formatCalendarTime(startTime)} – ${formatCalendarTime(endTime)}`;
};

export const formatDateLabel = (key: string): string => {
  const date = dateFromKey(key);
  return `${String(date.getDate()).padStart(2, '0')}/${String(
    date.getMonth() + 1,
  ).padStart(2, '0')}/${date.getFullYear()}`;
};

export const formatCalendarDate = formatDateLabel;

export const formatCalendarTitle = (
  anchorDate: string,
  view: CalendarViewMode,
): string => {
  const dates = getCalendarDateKeys(anchorDate, view);
  const start = dateFromKey(dates[0]);
  const end = dateFromKey(dates[dates.length - 1]);
  const monthFormatter = new Intl.DateTimeFormat('en-US', { month: 'short' });

  if (view === 'month') {
    return `${new Intl.DateTimeFormat('en-US', { month: 'long' }).format(start)} ${start.getFullYear()}`;
  }

  const startMonth = monthFormatter.format(start);
  const endMonth = monthFormatter.format(end);
  const startLabel = `${startMonth} ${start.getDate()}`;
  const endLabel = `${endMonth} ${end.getDate()}`;
  return `${startLabel} – ${endLabel}, ${end.getFullYear()}`;
};

export const formatWeekdayLabel = (key: string): string => {
  const date = dateFromKey(key);
  return CALENDAR_WEEKDAYS[date.getDay()].slice(0, 3);
};

export const isToday = (key: string): boolean =>
  key === dateKeyFromDate(new Date());

export const isItemInTimeGrid = (item: ClassCalendarItem): boolean => {
  const start = timeToMinutes(item.startTime);
  const end = timeToMinutes(item.endTime);
  return start !== null && end !== null && end > start;
};
